(function () {
  'use strict';

  function toCents(value) {
    const number = Number(value);
    return Number.isFinite(number) && number > 0
      ? Math.round(number)
      : 0;
  }

  function normalizeTiers(tiers) {
    const tierMap = new Map();

    (Array.isArray(tiers) ? tiers : []).forEach((tier) => {
      const threshold = toCents(tier && tier.threshold);
      const discount = toCents(tier && tier.discount);

      if (threshold > 0 && discount > 0) {
        tierMap.set(threshold, {
          threshold,
          discount
        });
      }
    });

    return Array.from(tierMap.values()).sort(
      (a, b) => a.threshold - b.threshold
    );
  }

  function calculateSavings({
    cartSubtotal = 0,
    variantPrice = 0,
    quantity = 1,
    tiers = []
  } = {}) {
    const normalizedTiers = normalizeTiers(tiers);

    const safeCartSubtotal = toCents(cartSubtotal);
    const safeVariantPrice = toCents(variantPrice);

    const parsedQuantity = Math.floor(Number(quantity));
    const safeQuantity =
      Number.isFinite(parsedQuantity) && parsedQuantity > 0
        ? parsedQuantity
        : 1;

    const projectedSubtotal =
      safeCartSubtotal + safeVariantPrice * safeQuantity;

    let unlockedTier = null;
    let nextTier = null;

    normalizedTiers.forEach((tier) => {
      if (projectedSubtotal >= tier.threshold) {
        unlockedTier = tier;
      } else if (!nextTier) {
        nextTier = tier;
      }
    });

    const discount = unlockedTier ? unlockedTier.discount : 0;
    const estimatedTotal = Math.max(
      0,
      projectedSubtotal - discount
    );

    const remainingToNextTier = nextTier
      ? Math.max(0, nextTier.threshold - projectedSubtotal)
      : 0;

    const segmentStart = unlockedTier
      ? unlockedTier.threshold
      : 0;

    const segmentLength = nextTier
      ? nextTier.threshold - segmentStart
      : 0;

    const progress =
      nextTier && segmentLength > 0
        ? Math.min(
            100,
            Math.max(
              0,
              ((projectedSubtotal - segmentStart) /
                segmentLength) *
                100
            )
          )
        : 100;

    return {
      projectedSubtotal,
      unlockedTier,
      nextTier,
      discount,
      estimatedTotal,
      remainingToNextTier,
      progress
    };
  }

  function formatUSD(cents) {
    return `$${(toCents(cents) / 100).toFixed(2)}`;
  }

  function buildMessage(result) {
    if (!result.nextTier) {
      const discount = result.unlockedTier
        ? result.unlockedTier.discount
        : 0;

      return `You unlocked the best deal: ${formatUSD(
        discount
      )} OFF!`;
    }

    if (!result.unlockedTier) {
      return `Add ${formatUSD(
        result.remainingToNextTier
      )} more to unlock ${formatUSD(
        result.nextTier.discount
      )} OFF!`;
    }

    return `You unlocked ${formatUSD(
      result.unlockedTier.discount
    )} OFF. Add ${formatUSD(
      result.remainingToNextTier
    )} more to unlock ${formatUSD(
      result.nextTier.discount
    )} OFF!`;
  }

  class TieredSavingsModal extends HTMLElement {
    constructor() {
      super();

      this.handleClick = this.handleClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleVariantChange =
        this.handleVariantChange.bind(this);
      this.handleQuantityChange =
        this.handleQuantityChange.bind(this);
    }

    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.sectionId = String(this.dataset.sectionId || '');
      this.productId = String(this.dataset.productId || '');
      this.productSection = this.closest('.product-section');
      this.variantPrice = toCents(
        this.dataset.initialVariantPrice
      );

      this.cartSubtotal = 0;
      this.cartRequest = null;
      this.lastTrigger = null;
      this.closeTimer = null;

      this.dialog = this.querySelector(
        '[data-tiered-savings-dialog]'
      );

      this.closeButton = this.querySelector(
        '[data-tiered-savings-close]'
      );

      this.loadingElement = this.querySelector(
        '[data-tiered-savings-loading]'
      );

      this.contentElement = this.querySelector(
        '[data-tiered-savings-content]'
      );

      this.messageElement = this.querySelector(
        '[data-tiered-savings-message]'
      );

      this.progressElement = this.querySelector(
        '[data-tiered-savings-progress]'
      );

      this.progressBarElement = this.querySelector(
        '[data-tiered-savings-progress-bar]'
      );

      this.subtotalElement = this.querySelector(
        '[data-tiered-savings-subtotal]'
      );

      this.discountElement = this.querySelector(
        '[data-tiered-savings-discount]'
      );

      this.totalElement = this.querySelector(
        '[data-tiered-savings-total]'
      );

      this.errorElement = this.querySelector(
        '[data-tiered-savings-error]'
      );

      this.config = this.readConfig();
      this.tiers = normalizeTiers(this.config.tiers);

      document.addEventListener('click', this.handleClick);
      document.addEventListener(
        'keydown',
        this.handleKeydown
      );

      if (this.productSection) {
  this.productSection.addEventListener(
    'variantChange',
    this.handleVariantChange
  );
}

      document.addEventListener(
        'change',
        this.handleQuantityChange
      );

      if (!this.tiers.length) {
        this.hideSaleButtons();
      }
    }

    disconnectedCallback() {
      document.removeEventListener(
        'click',
        this.handleClick
      );

      document.removeEventListener(
        'keydown',
        this.handleKeydown
      );

     if (this.productSection) {
  this.productSection.removeEventListener(
    'variantChange',
    this.handleVariantChange
  );
}

      document.removeEventListener(
        'change',
        this.handleQuantityChange
      );

      clearTimeout(this.closeTimer);
    }

    readConfig() {
      const configElement = this.querySelector(
        '[data-tiered-savings-config]'
      );

      if (!configElement) {
        return { tiers: [] };
      }

      try {
        return JSON.parse(configElement.textContent);
      } catch (error) {
        console.error(
          'Tiered savings configuration is invalid.',
          error
        );

        return { tiers: [] };
      }
    }

    hideSaleButtons() {
      document
        .querySelectorAll(
          `[data-tiered-savings-open="${this.id}"]`
        )
        .forEach((button) => {
          button.hidden = true;
        });
    }

    handleClick(event) {
      if (!(event.target instanceof Element)) return;

      const trigger = event.target.closest(
        `[data-tiered-savings-open="${this.id}"]`
      );

      if (trigger) {
        event.preventDefault();
        this.open(trigger);
        return;
      }

      const closeButton = event.target.closest(
        '[data-tiered-savings-close]'
      );

      if (closeButton && this.contains(closeButton)) {
        this.close();
        return;
      }

      const overlay = this.querySelector(
        '[data-tiered-savings-overlay]'
      );

      if (event.target === overlay) {
        this.close();
      }
    }

    handleKeydown(event) {
      if (!this.hasAttribute('open')) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusableElements = Array.from(
        this.dialog.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((element) => !element.hidden);

      if (!focusableElements.length) {
        event.preventDefault();
        this.dialog.focus();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement =
        focusableElements[focusableElements.length - 1];

      if (
        event.shiftKey &&
        document.activeElement === firstElement
      ) {
        event.preventDefault();
        lastElement.focus();
      } else if (
        !event.shiftKey &&
        document.activeElement === lastElement
      ) {
        event.preventDefault();
        firstElement.focus();
      }
    }

handleVariantChange(event) {
  const detail = event.detail || {};

  if (!detail.variant) {
    return;
  }

  this.variantPrice = toCents(detail.variant.price);

      if (
        this.hasAttribute('open') &&
        this.contentElement &&
        !this.contentElement.hidden
      ) {
        this.render(false);
      }
    }

    handleQuantityChange(event) {
     const quantityInput = this.productSection
  ? this.productSection.querySelector('[name="quantity"]')
  : null;

      if (event.target !== quantityInput) return;

      if (
        this.hasAttribute('open') &&
        this.contentElement &&
        !this.contentElement.hidden
      ) {
        this.render(false);
      }
    }

    open(trigger) {
      if (!this.tiers.length) return;

      clearTimeout(this.closeTimer);

      this.lastTrigger = trigger;
      trigger.setAttribute('aria-expanded', 'true');

      this.hidden = false;
      document.body.classList.add(
        'tiered-savings-open'
      );

      requestAnimationFrame(() => {
        this.setAttribute('open', '');
      });

      this.setLoading(true);

      if (this.closeButton) {
        this.closeButton.focus({
          preventScroll: true
        });
      }

      this.loadCart()
        .then((cart) => {
          this.cartSubtotal = toCents(
            cart.items_subtotal_price
          );

          this.render(false);
        })
        .catch((error) => {
          console.error(
            'Tiered savings could not load the cart.',
            error
          );

          this.cartSubtotal = 0;
          this.render(true);
        });
    }

    close() {
      if (!this.hasAttribute('open')) return;

      this.removeAttribute('open');

      document.body.classList.remove(
        'tiered-savings-open'
      );

      const currentTrigger = document.querySelector(
        `[data-tiered-savings-open="${this.id}"]`
      );

      if (currentTrigger) {
        currentTrigger.setAttribute(
          'aria-expanded',
          'false'
        );
      }

      const focusTarget =
        this.lastTrigger && this.lastTrigger.isConnected
          ? this.lastTrigger
          : currentTrigger;

      this.closeTimer = setTimeout(() => {
        this.hidden = true;

        if (focusTarget) {
          focusTarget.focus({
            preventScroll: true
          });
        }
      }, 180);
    }

    loadCart() {
      if (this.cartRequest) {
        return this.cartRequest;
      }

      const root =
        window.Shopify &&
        window.Shopify.routes &&
        window.Shopify.routes.root
          ? window.Shopify.routes.root
          : '/';

      this.cartRequest = fetch(`${root}cart.js`, {
        method: 'GET',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json'
        }
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(
              `Cart request failed: ${response.status}`
            );
          }

          return response.json();
        })
        .finally(() => {
          this.cartRequest = null;
        });

      return this.cartRequest;
    }

    getQuantity() {
    const quantityInput = this.productSection
  ? this.productSection.querySelector('[name="quantity"]')
  : null;

      const parsedQuantity = quantityInput
        ? Math.floor(Number(quantityInput.value))
        : 1;

      return Number.isFinite(parsedQuantity) &&
        parsedQuantity > 0
        ? parsedQuantity
        : 1;
    }

    setLoading(isLoading) {
      if (this.loadingElement) {
        this.loadingElement.hidden = !isLoading;
      }

      if (this.contentElement) {
        this.contentElement.hidden = isLoading;
      }
    }

    render(cartUnavailable) {
      const result = calculateSavings({
        cartSubtotal: this.cartSubtotal,
        variantPrice: this.variantPrice,
        quantity: this.getQuantity(),
        tiers: this.tiers
      });

      this.messageElement.textContent =
        buildMessage(result);

      this.subtotalElement.textContent = formatUSD(
        result.projectedSubtotal
      );

      this.discountElement.textContent = `−${formatUSD(
        result.discount
      )}`;

      this.totalElement.textContent = formatUSD(
        result.estimatedTotal
      );

      this.progressBarElement.style.width =
        `${result.progress}%`;

      this.progressElement.setAttribute(
        'aria-valuenow',
        String(Math.round(result.progress))
      );

      this.progressElement.setAttribute(
        'aria-valuetext',
        buildMessage(result)
      );

      this.querySelectorAll(
        '[data-tier-threshold]'
      ).forEach((tierElement) => {
        const threshold = toCents(
          tierElement.dataset.tierThreshold
        );

        const isUnlocked =
          result.projectedSubtotal >= threshold;

        const isActive = Boolean(
          result.unlockedTier &&
            result.unlockedTier.threshold === threshold
        );

        tierElement.classList.toggle(
          'is-unlocked',
          isUnlocked
        );

        tierElement.classList.toggle(
          'is-active',
          isActive
        );

        if (isActive) {
          tierElement.setAttribute(
            'aria-current',
            'step'
          );
        } else {
          tierElement.removeAttribute('aria-current');
        }
      });

      if (this.errorElement) {
        this.errorElement.hidden = !cartUnavailable;
      }

      this.setLoading(false);
    }
  }

  if (!customElements.get('tiered-savings-modal')) {
    customElements.define(
      'tiered-savings-modal',
      TieredSavingsModal
    );
  }
})();